import React, { useEffect } from 'react'
import Snackbar from '@mui/material/Snackbar'

import FeedbackAlert from './FeedbackAlert'
import { FEEDBACK_ALERT_DURATION_MS } from './constants/feedbackAlertConstants'
import {
  buildFeedbackAlertSx,
  resolveFeedbackAlertStyle
} from './helpers/feedbackAlertHelpers'

export const TIMED_TOAST_ALERT_DURATION_MS = FEEDBACK_ALERT_DURATION_MS

/**
 * Wrapper para mostrar feedback temporal.
 *
 * Usa `variant="toast"` para mostrarlo flotando en la esquina.
 * Usa `variant="inline"` para mostrarlo dentro del flujo normal de la pantalla.
 */
export default function TimedToastAlert({
  id,
  message,
  severity = 'error',
  icon,
  colors,
  onClose,
  durationMs = TIMED_TOAST_ALERT_DURATION_MS,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  open = true,
  variant = 'toast',
  className
}) {
  useEffect(() => {
    if (variant !== 'inline' || !open || !message || typeof onClose !== 'function') return undefined

    const timeoutId = window.setTimeout(() => {
      onClose()
    }, durationMs)

    return () => window.clearTimeout(timeoutId)
  }, [durationMs, id, message, onClose, open, variant])

  if (!open || !message) return null

  const style = resolveFeedbackAlertStyle(severity, colors)
  const alertNode = (
    <FeedbackAlert
      id={id}
      message={message}
      severity={severity}
      icon={icon}
      colors={colors}
      onClose={onClose}
      className={className}
      durationMs={durationMs}
      sx={buildFeedbackAlertSx(style, durationMs, {
        inline: variant === 'inline',
        progressKeyframeName: 'timedToastAlertProgress'
      })}
    />
  )

  if (variant === 'inline') {
    return alertNode
  }

  return (
    <Snackbar
      open={open}
      key={id}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return
        onClose?.()
      }}
      anchorOrigin={anchorOrigin}
      autoHideDuration={durationMs}
      sx={{
        top: { xs: 16, sm: 24 },
        right: { xs: 12, sm: 24 },
        left: { xs: 12, sm: 'auto' },
        width: { xs: 'calc(100% - 24px)', sm: 480 },
        maxWidth: { xs: 'calc(100% - 24px)', sm: 520 }
      }}
    >
      {alertNode}
    </Snackbar>
  )
}
